import { getLang, tr } from "./i18n";

export const NOLEGGIO_URL = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "mn_auth_token";

let token = localStorage.getItem(TOKEN_KEY);
let onUnauthorized = null;

export const getToken = () => token;
export const setUnauthorizedHandler = (fn) => (onUnauthorized = fn);
export function persistToken(t) {
  token = t;
  if (t) localStorage.setItem(TOKEN_KEY, t);
  else localStorage.removeItem(TOKEN_KEY);
}

export const imageUri = (url) => (url && url.startsWith("/api/") ? `${NOLEGGIO_URL}${url}` : url);

function errMsg(data) {
  if (typeof data?.detail === "string") return data.detail;
  return data?.detail ? tr("invalid_data") : tr("generic_error");
}

export async function api(path, { method = "GET", body } = {}) {
  const headers = { "Content-Type": "application/json", "Accept-Language": getLang() };
  const sent = token;
  if (sent) headers.Authorization = `Bearer ${sent}`;
  const res = await fetch(`${NOLEGGIO_URL}/api${path}`, { method, headers, body: body ? JSON.stringify(body) : undefined });
  const text = await res.text();
  const data = text ? JSON.parse(text) : null;
  if (!res.ok) {
    if (res.status === 401 && sent && sent === token) onUnauthorized?.();
    const e = new Error(errMsg(data));
    e.status = res.status;
    throw e;
  }
  return data;
}

export async function uploadFile(path, file) {
  const form = new FormData();
  form.append("file", file, file.name || "foto.jpg");
  const res = await fetch(`${NOLEGGIO_URL}/api${path}`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}`, "Accept-Language": getLang() },
    body: form,
  });
  const text = await res.text();
  const data = text ? JSON.parse(text) : null;
  if (!res.ok) throw new Error(typeof data?.detail === "string" ? data.detail : tr("upload_failed"));
  return data;
}

export async function scanIdDocument(front, back) {
  const form = new FormData();
  form.append("front", front, front.name || "fronte.jpg");
  form.append("back", back, back.name || "retro.jpg");
  const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/id-scan`, { method: "POST", headers: { Authorization: `Bearer ${token}` }, body: form });
  const data = await res.json().catch(() => null);
  if (!res.ok) throw new Error(typeof data?.detail === "string" ? data.detail : tr("scan_failed"));
  return data;
}

export async function fetchReviews() {
  const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/reviews`);
  if (!res.ok) throw new Error(tr("load_error"));
  return res.json();
}

export async function saveReviews(body) {
  const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/reviews`, {
    method: "PUT", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` }, body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => null);
  if (!res.ok) throw new Error(typeof data?.detail === "string" ? data.detail : tr("invalid_data"));
  return data;
}

export async function fetchCalendar(month) {
  const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/calendar?month=${month}`);
  if (!res.ok) throw new Error(tr("load_error"));
  return res.json();
}

export async function downloadContract(booking) {
  const res = await fetch(`${NOLEGGIO_URL}/api/bookings/${booking.id}/contract.pdf`, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) throw new Error(tr("pdf_error"));
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement("a");
  a.href = url;
  a.download = `contratto-${booking.sheet_id || booking.id.slice(0, 8)}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10000);
}

/** Last DD/MM/YYYY found in the "issued by and expiry" text, as YYYY-MM-DD. */
export function expiryIso(text) {
  const all = [...(text || "").matchAll(/(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})/g)];
  if (!all.length) return "";
  const [, d, m, y] = all[all.length - 1];
  return `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`;
}

export const SIZES = ["S", "M", "L", "XL"];
export const SIZE_HEIGHTS = { S: "155-165 cm", M: "165-175 cm", L: "175-185 cm", XL: "185-195 cm" };
export const CATEGORIES = ["gravel", "egravel", "ebike_mtb"];
export const CATEGORY_LABELS = { gravel: "Gravel", egravel: "E-gravel", ebike_mtb: "E-bike MTB" };
export const CATEGORY_PRICES = { gravel: 40, egravel: 50, ebike_mtb: 60 };
export const SPEC_KEYS = ["frame", "motor", "battery", "range", "suspension", "groupset", "brakes", "wheels", "weight"];
export const OPEN_HOUR = 9;
export const CLOSE_HOUR = 19;

export const SHOP_ADDRESS = "Contrada Le Grazie 43, Tolentino (MC)";
export const MAPS_URL = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent("Contrada Le Grazie 43, 62029 Tolentino MC, Italia")}`;
export const whatsappUrl = () => `https://wa.me/393289832243?text=${encodeURIComponent(tr("whatsapp_text"))}`;
export const STRAVA_URL = "https://www.strava.com/athlete/routes";
export const INSTAGRAM_URL = "https://www.instagram.com/chientibike/";

const DAYS = { it: ["Dom", "Lun", "Mar", "Mer", "Gio", "Ven", "Sab"], en: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"] };
const MONTHS = {
  it: ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"],
  en: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
};

export function toISODate(d) {
  return `${d.getFullYear()}-${`${d.getMonth() + 1}`.padStart(2, "0")}-${`${d.getDate()}`.padStart(2, "0")}`;
}

export function nextDays(n) {
  const base = new Date();
  const l = getLang();
  return Array.from({ length: n }, (_, i) => {
    const d = new Date(base.getFullYear(), base.getMonth(), base.getDate() + i);
    return { iso: toISODate(d), dow: i === 0 ? tr("today") : DAYS[l][d.getDay()], day: d.getDate(), month: MONTHS[l][d.getMonth()] };
  });
}

export const euro = (v) => `€ ${Number(v).toFixed(2).replace(".", ",")}`;
export const fmtDays = (n) => tr(n === 1 ? "day_one" : "day_many", { n });

function fmtDate(s) {
  const l = getLang();
  const [y, m, d] = s.split("T")[0].split("-").map(Number);
  return `${DAYS[l][new Date(y, m - 1, d).getDay()]} ${d} ${MONTHS[l][m - 1]}`;
}
export const fmtRange = (b) => `${fmtDate(b.start)} → ${fmtDate(b.end)}`;

export function specValue(v) {
  const parts = v.split(" / ");
  if (parts.length !== 2) return v;
  return getLang() === "en" ? parts[1] : parts[0];
}

export const bikeDescription = (b) => (getLang() === "en" && b.description_en ? b.description_en : b.description);

export const isEmbedded = () => {
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
};
